import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, ChatText, Trash, Clock, X
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const ConversationThreads = ({ onSelectThread, activeThreadId, onNewThread, onClose }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = BACKEND_URL;

  const fetchThreads = useCallback(async () => {
    if (!API) return;
    try {
      const res = await fetch(`${API}/api/command/conversations?limit=20`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setThreads(data.conversations || []);
      }
    } catch (e) {
      console.error('Failed to fetch threads:', e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const deleteThread = async (e, convId) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/command/conversations/${convId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setThreads(prev => prev.filter(t => t.conversation_id !== convId));
        if (activeThreadId === convId) onNewThread();
      }
    } catch (e) {
      console.error('Failed to delete thread:', e);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full" data-testid="conversation-threads">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <ChatText size={16} className="text-[var(--brand-primary)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Threads</h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--surface-3)] text-[var(--text-muted)]">
            {threads.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewThread}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-3)] transition-colors text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
            data-testid="new-thread-btn"
            title="New thread"
          >
            <Plus size={14} weight="bold" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-3)] transition-colors text-[var(--text-muted)]"
            data-testid="close-threads-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-4 h-4 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-8 px-4">
            <ChatText size={24} className="text-[var(--text-muted)] mx-auto mb-2 opacity-40" />
            <p className="text-xs text-[var(--text-muted)]">No conversation history yet</p>
          </div>
        ) : (
          <div className="py-1">
            {threads.map(thread => {
              const isActive = thread.conversation_id === activeThreadId;
              return (
                <div
                  key={thread.conversation_id}
                  onClick={() => onSelectThread(thread.conversation_id)}
                  role="button"
                  tabIndex={0}
                  className={`w-full text-left px-4 py-2.5 group transition-all relative cursor-pointer ${
                    isActive
                      ? 'bg-[var(--brand-primary)]/8 border-l-2 border-l-[var(--brand-primary)]'
                      : 'hover:bg-[var(--surface-2)] border-l-2 border-l-transparent'
                  }`}
                  data-testid={`thread-${thread.conversation_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-xs font-medium line-clamp-2 ${
                      isActive ? 'text-[var(--brand-primary)]' : 'text-[var(--text-primary)]'
                    }`}>
                      {thread.title || 'New conversation'}
                    </p>
                    <button
                      onClick={(e) => deleteThread(e, thread.conversation_id)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400 transition-all shrink-0"
                      data-testid={`delete-thread-${thread.conversation_id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock size={9} className="text-[var(--text-muted)]" />
                    <span className="text-[9px] text-[var(--text-muted)]">
                      {formatTime(thread.updated_at)}
                    </span>
                    {thread.message_count > 0 && (
                      <span className="text-[9px] text-[var(--text-muted)]">
                        {thread.message_count} msg{thread.message_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationThreads;
